import { useState } from "react";
import { isNewOrder } from "@/lib/orderDetails";
import { OrderCard } from "@/components/admin/OrderCard";
import { Input } from "@/components/ui/input";
import { Search, X } from "lucide-react";

const digits = (s) => String(s || "").replace(/\D/g, "");
const matches = (o, q) => {
  const t = q.trim().toLowerCase().replace(/^#/, "");
  if (!t) return true;
  const d = digits(t);
  return (o.customer_name || "").toLowerCase().includes(t)
    || o.id.toLowerCase().includes(t)
    || (d.length >= 3 && digits(o.customer_phone).includes(d));
};

const FILTERS = [
  { id: "new", label: "New", test: isNewOrder },
  { id: "active", label: "Active", test: (o) => o.payment_status !== "pending" && !["placed", "delivered"].includes(o.status) },
  { id: "completed", label: "Completed", test: (o) => o.status === "delivered" },
  { id: "missing_time", label: "Missing time", test: (o) => o.missing_time && o.payment_status !== "pending" && o.status !== "delivered" },
  { id: "unpaid", label: "Unpaid", test: (o) => o.payment_status === "pending" },
  { id: "all", label: "All", test: () => true },
];

export const OrdersList = ({ orders }) => {
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const f = FILTERS.find((x) => x.id === filter);
  const searching = q.trim().length > 0;
  const shown = searching ? orders.filter((o) => matches(o, q)) : orders.filter(f.test);
  return (
    <div className="mt-6">
      <div className="relative mb-4 max-w-md">
        <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sugar-muted" />
        <Input data-testid="admin-order-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, phone or order #" className="rounded-full border-sugar-border bg-white pl-9 pr-9" />
        {searching && <button data-testid="admin-order-search-clear" onClick={() => setQ("")} aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 text-sugar-muted hover:text-sugar-pink"><X size={16} /></button>}
      </div>
      {searching && <p data-testid="admin-order-search-count" className="mb-3 text-xs font-semibold text-sugar-ink2">{shown.length} {shown.length === 1 ? "order" : "orders"} matching “{q.trim()}” across all orders</p>}
      <div className={`flex flex-wrap gap-2 ${searching ? "opacity-40" : ""}`}>
        {FILTERS.map((x) => {
          const n = orders.filter(x.test).length;
          const on = filter === x.id;
          return (
            <button key={x.id} data-testid={`admin-filter-${x.id}`} onClick={() => { setFilter(x.id); setQ(""); }}
              className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${on ? (x.id === "new" ? "bg-yellow-300 text-sugar-ink" : "bg-sugar-ink text-white") : "border border-sugar-border bg-white text-sugar-ink2"}`}>
              {x.label} ({n})
            </button>
          );
        })}
      </div>
      <div className="mt-4 space-y-3">
        {shown.length === 0 && <p data-testid="admin-orders-empty" className="py-10 text-center text-sugar-ink2">{searching ? "No orders match your search." : <>No {f.id === "all" ? "" : `${f.label.toLowerCase()} `}orders{f.id === "all" ? " yet" : ""}.</>}</p>}
        {shown.map((o) => <OrderCard key={o.id} o={o} />)}
      </div>
    </div>
  );
};
